import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHome, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import OffersDealsShowcase, { resolveOfferPropertyId } from '../components/OffersDealsShowcase';
import { getApiUrl } from '../config/api';
import './OffersDealsPage.css';

const defaultOffers = [
  { id: 'offer-1', badge: 'Limited Time Offer', title: 'Business Stay Offer', description: 'Perfect stay for business travelers.', details: 'Early check-in • 15% off', pricePerNight: 1720, originalPrice: 2220, image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200&q=80' },
  { id: 'offer-2', badge: 'Early Bird Special', title: 'Early Bird Special', description: 'Book early and enjoy exclusive savings.', details: 'Late check-out • 20% off', pricePerNight: 1989, originalPrice: 2340, image: 'https://images.unsplash.com/photo-1445019980597-93fa8acb246c?w=1200&q=80' },
];

const sortOffersNewestFirst = (offers = []) => {
  const toTs = (offer) => {
    const raw =
      offer?.createdAt ||
      offer?.updatedAt ||
      offer?.discount?.createdAt ||
      offer?.discount?.updatedAt ||
      offer?.property?.createdAt ||
      offer?.property?.updatedAt;
    const ts = raw ? new Date(raw).getTime() : 0;
    return Number.isFinite(ts) ? ts : 0;
  };

  return [...offers].sort((a, b) => toTs(b) - toTs(a));
};

const ITEMS_PER_PAGE = 4;

const OffersDealsPage = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setLoading(true);
        const response = await fetch(getApiUrl('/api/properties/offers-with-discounts'));
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          setOffers(defaultOffers);
        } else {
          const result = await response.json();
          if (result.success && result.offers && result.offers.length > 0) {
            setOffers(sortOffersNewestFirst(result.offers));
          } else {
            setOffers(defaultOffers);
          }
        }
      } catch (error) {
        console.error('Error fetching offers:', error);
        setOffers(defaultOffers);
      } finally {
        setLoading(false);
      }
    };
    fetchOffers();
  }, []);

  const totalPages = Math.ceil(offers.length / ITEMS_PER_PAGE);
  const paginatedOffers = offers.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  );

  const handlePrevious = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  const handleViewProperty = (item) => {
    const propertyId = resolveOfferPropertyId(item);
    if (propertyId) {
      navigate(`/property/${propertyId}`);
    }
  };

  return (
    <div className="offers-deals-page">
      <Navbar />
      <div className="offers-deals-container">
        <div className="offers-deals-page-toolbar">
          <button type="button" className="btn-back" onClick={() => navigate(-1)} aria-label="Go back">
            ← Back
          </button>
          <button type="button" className="btn-home" onClick={() => navigate('/')}>
            <FaHome /> Home
          </button>
        </div>

        <OffersDealsShowcase
          className="offers-deals-showcase--page"
          offers={paginatedOffers}
          loading={loading}
          showViewAll={false}
          onOfferClick={handleViewProperty}
        />

        {totalPages > 1 && !loading && offers.length > 0 && (
          <div className="offers-deals-pagination">
            <button
              type="button"
              className="offers-deals-page-btn offers-deals-page-btn-prev"
              onClick={handlePrevious}
              disabled={currentPage === 0}
            >
              <FaChevronLeft /> Previous
            </button>
            <span className="offers-deals-page-info">
              {currentPage + 1} / {totalPages}
            </span>
            <button
              type="button"
              className="offers-deals-page-btn offers-deals-page-btn-next"
              onClick={handleNext}
              disabled={currentPage === totalPages - 1}
            >
              Next <FaChevronRight />
            </button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default OffersDealsPage;
